import * as React from 'react';
import { observer } from 'mobx-react';
import { Button, IconButton, Table, formatDate, formatDateTime } from '@ixicare/ui';
import { ApiKeyEntryProps, ApiKeyType } from '@ixicare/resources';
import { Plus, Trash2 } from 'lucide-react';
import { t } from '~i18n';
import { Spacer } from '~components/Utils/Spacer';
import { SectionTitle } from '~components/Utils/Section';
import { useStores } from '~store/utils';
import { useSiteApiKeyMutations, useSiteApiKeys } from '../hooks/useSiteApiKeys';
import { CreateApiKeyModal } from './SiteModals/CreateApiKeyModal';

const EMPTY = '—';
// Name the backend gives tokens imported from third-party-ds (IMPORTED_WEBSOCKET_KEY_NAME). They are
// not `ixiv1_<prefix>_…` keys and their prefix is made up, so showing one would be misleading.
const IMPORTED_KEY_NAME = 'Imported websocket token';

const keyTypeLabel = (keyType: ApiKeyType) =>
  keyType === ApiKeyType.WEBSOCKET ? t('site.developer.keyType.websocket') : t('site.developer.keyType.api');

/** The keys other systems use to reach this site: public API keys and Websocket keys. */
export const SiteApiKeysSetting = observer(({ siteUuid }: { siteUuid: string }) => {
  const { confirmation } = useStores();
  const { data: apiKeys } = useSiteApiKeys(siteUuid);
  const { remove } = useSiteApiKeyMutations(siteUuid);
  const [isCreating, setCreating] = React.useState(false);

  const confirmDelete = (apiKey: ApiKeyEntryProps) => {
    confirmation.show({
      title: t('site.developer.keys.deleteConfirmTitle', { name: apiKey.name ?? apiKey.prefix }),
      message: t('site.developer.keys.deleteConfirmMessage'),
      confirmLabel: t('residents.table.delete'),
      dangerous: true,
      onConfirm: () => remove.mutate(apiKey.prefix),
    });
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <SectionTitle style={{ marginBottom: 0 }}>{t('site.developer.keys.title')}</SectionTitle>
        <Button lucide={Plus} onClick={() => setCreating(true)}>
          {t('site.developer.keys.create')}
        </Button>
      </div>
      <p className="text-muted">{t('site.developer.keys.description')}</p>
      <Spacer size={16} />
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeadCol>{t('billing.table.name')}</Table.HeadCol>
            <Table.HeadCol>{t('billing.table.type')}</Table.HeadCol>
            <Table.HeadCol>{t('site.developer.keys.key')}</Table.HeadCol>
            <Table.HeadCol>{t('site.developer.keys.created')}</Table.HeadCol>
            <Table.HeadCol>{t('site.developer.keys.lastUsed')}</Table.HeadCol>
            <Table.HeadCol>{t('healthcareProviders.sessions.expires')}</Table.HeadCol>
            <Table.HeadCol>{t('site.developer.keys.createdBy')}</Table.HeadCol>
            <Table.HeadCol>{''}</Table.HeadCol>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {apiKeys?.map((apiKey) => (
            <Table.Row key={apiKey.prefix}>
              <Table.Col>{apiKey.name ?? EMPTY}</Table.Col>
              <Table.Col>{keyTypeLabel(apiKey.keyType)}</Table.Col>
              <Table.Col>
                {apiKey.name === IMPORTED_KEY_NAME ? EMPTY : <code>{`ixiv1_${apiKey.prefix}_…`}</code>}
              </Table.Col>
              <Table.Col>{formatDate(apiKey.created)}</Table.Col>
              <Table.Col>
                {apiKey.lastUsedAt ? formatDateTime(apiKey.lastUsedAt) : t('device-health.never-seen')}
              </Table.Col>
              <Table.Col>
                {apiKey.isExpired ? (
                  <span style={{ color: 'var(--color-danger)' }}>{t('site.developer.keys.expired')}</span>
                ) : apiKey.expiresAt ? (
                  formatDate(apiKey.expiresAt)
                ) : (
                  t('device-health.never-seen')
                )}
              </Table.Col>
              <Table.Col>
                {apiKey.createdBy ? `${apiKey.createdBy.firstName} ${apiKey.createdBy.lastName}` : EMPTY}
              </Table.Col>
              <Table.Col>
                <span title={t('residents.table.delete')}>
                  <IconButton lucide={Trash2} onClick={() => confirmDelete(apiKey)} />
                </span>
              </Table.Col>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
      {apiKeys?.length === 0 && <p>{t('site.developer.keys.empty')}</p>}
      {isCreating && <CreateApiKeyModal siteUuid={siteUuid} onClose={() => setCreating(false)} />}
    </>
  );
});
